import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    ViewChild,
} from '@angular/core';
import {
    ConvocationRowItem,
    isConvocationDraft,
} from '../mister-convocation-shared';

@Component({
    selector: 'app-dashboard-mister-convocations-row',
    templateUrl: './convocations-row.component.html',
    styleUrls: ['./convocations-row.component.scss'],
})
export class DashboardMisterConvocationsRowComponent
    implements AfterViewInit, OnDestroy
{
    @ViewChild('convocationsScrollList', { read: ElementRef })
    scrollListEl?: ElementRef<HTMLElement>;

    @Input() convocations: ConvocationRowItem[] = [];
    @Input() loading = false;
    @Input() showCreateButton = true;
    @Input() heading = 'Ultime convocazioni';
    @Input() emptyMessage =
        'Non hai ancora convocazioni. Crea la prima con il pulsante sopra.';
    /** Router link prefix for convocation detail, e.g. `['/users/convocations']`. */
    @Input() detailRouteBase: string[] = ['/dashboard/mister/convocations'];
    @Input() showDraftActions = false;
    @Input() statusUpdatingId: string | null = null;

    /** Placeholder count for horizontal skeleton cards */
    readonly skeletonCardPlaceholders = [0, 1, 2];

    @Output() openCreate = new EventEmitter<void>();
    @Output() approveDraft = new EventEmitter<ConvocationRowItem>();
    @Output() cancelDraft = new EventEmitter<ConvocationRowItem>();

    canScrollLeft = false;
    canScrollRight = false;

    private resizeObserver?: ResizeObserver;

    ngAfterViewInit(): void {
        queueMicrotask(() => this.updateScrollState());
        const el = this.scrollListEl?.nativeElement;
        if (el && typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(() =>
                this.updateScrollState(),
            );
            this.resizeObserver.observe(el);
        }
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
    }

    scrollList(direction: 'left' | 'right'): void {
        const el = this.scrollListEl?.nativeElement;
        if (!el) {
            return;
        }
        const step = Math.max(240, Math.floor(el.clientWidth * 0.85));
        el.scrollBy({
            left: direction === 'left' ? -step : step,
            behavior: 'smooth',
        });
    }

    updateScrollState(): void {
        const el = this.scrollListEl?.nativeElement;
        if (!el) {
            this.canScrollLeft = false;
            this.canScrollRight = false;
            return;
        }
        const { scrollLeft, scrollWidth, clientWidth } = el;
        const maxScroll = scrollWidth - clientWidth;
        const epsilon = 2;
        this.canScrollLeft = scrollLeft > epsilon;
        this.canScrollRight = scrollLeft < maxScroll - epsilon;
    }

    statusClass(status: string): string {
        const key = status.toLowerCase();
        if (
            key.includes('pubblicat') ||
            key.includes('open') ||
            key.includes('attiv') ||
            key === 'active'
        ) {
            return '!bg-emerald-50 !text-emerald-800 !ring-emerald-600/15';
        }
        if (key.includes('bozz') || key.includes('draft')) {
            return '!bg-amber-50 !text-amber-800 !ring-amber-600/15';
        }
        if (
            key.includes('chius') ||
            key.includes('annull') ||
            key.includes('cancell')
        ) {
            return '!bg-zinc-100 !text-zinc-700 !ring-zinc-500/10';
        }
        return '!bg-sky-50 !text-sky-800 !ring-sky-600/15';
    }

    isLink(value: string): boolean {
        return /^https?:\/\//i.test(value?.trim() ?? '');
    }

    isDraft(status: string): boolean {
        return isConvocationDraft(status);
    }

    isUpdating(id: string): boolean {
        return this.statusUpdatingId === id;
    }

    onApproveDraft(c: ConvocationRowItem, event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        this.approveDraft.emit(c);
    }

    onCancelDraft(c: ConvocationRowItem, event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        this.cancelDraft.emit(c);
    }

    trackById(_index: number, item: ConvocationRowItem): string {
        return item.id;
    }
}
